import type { IVisitorLogFilters, IVisitorLogExportFilters } from '@/types/hvl/filters'
import type { CreateVisitorLogForm, SignOutVisitorLogForm, UpdateVisitorLogForm } from '@/types/hvl/forms'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import axios, { AxiosError } from 'axios'

/**
 * Gets all visitor log statuses.
 *
 * @returns {Promise<any>} an array of statuses.
 * @throws {AxiosError}
 */

export const getAllLogStatuses = async (): Promise<string[]> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_HVL_VISITOR_LOGS_ENDPOINT}/status`)
  return res.data
}

/**
 * Gets all visitor logs with optional query parameters.
 *
 * @param params {fragment: search query, page: request page, per_page: page size}
 * @returns {Promise<any>} an array of visitor logs.
 * @throws {AxiosError}
 */
export async function getAllVisitorLogs(params: IVisitorLogFilters): Promise<any> {
  const res = await axios.get(import.meta.env.VITE_APP_HVL_VISITOR_LOGS_ENDPOINT, {
    params,
  })
  return res.data
}

/**
 * Creates a visitor log.
 *
 * @param form create visitor log form.
 * @returns {Promise<any>} created visitor log.
 * @throws {AxiosError}
 */
export async function createVisitorLog(form: CreateVisitorLogForm): Promise<any> {
  const res = await axios.post(import.meta.env.VITE_APP_HVL_VISITOR_LOGS_ENDPOINT, form)
  return res.data
}

/**
 * Updates a visitor log.
 *
 * @param form update visitor log form.
 * @returns {Promise<any>} updated visitor log.
 * @throws {AxiosError}
 */
export async function updateVisitorLog(form: UpdateVisitorLogForm): Promise<any> {
  const res = await axios.put(import.meta.env.VITE_APP_HVL_VISITOR_LOGS_ENDPOINT, form)
  return res.data
}

/**
 * Sign out a visitor log.
 *
 * @param logId visitor log ID.
 * @param form sign out visitor log form.
 * @returns {Promise<any>} updated visitor log.
 * @throws {AxiosError}
 */
export async function signOutVisitorLog(logId: number, form: SignOutVisitorLogForm): Promise<any> {
  const res = await axios.patch(`${import.meta.env.VITE_APP_HVL_VISITOR_LOGS_ENDPOINT}/${logId}/sign-out`, form)
  return res.data
}

/**
 * Deletes a visitor log given a log ID.
 */
export async function deleteVisitorLog(logId: number): Promise<void> {
  const params = { id: logId }
  await axios.delete(`${import.meta.env.VITE_APP_HVL_VISITOR_LOGS_ENDPOINT}`, { data: params })
}

/**
 * Gets visitor logs CSV.
 *
 * @param params
 * @returns {Promise<any>} a Blob of CSV data.
 * @throws {AxiosError}
 */
export async function getVisitorLogsExport(params: IVisitorLogExportFilters): Promise<any> {
  const res = await axios.get(`${import.meta.env.VITE_APP_HVL_VISITOR_LOGS_ENDPOINT}/export`, {
    params,
    responseType: 'blob',
  })

  return res.data as Blob
}
